import React, {useMemo, useState} from "react";
import NovelStoryWorkspaceNavigator from "./NovelStoryWorkspaceNavigator";
import NextScenesBookBlueprintNavigator from "./NextScenesBookBlueprintNavigator";
import {NovelStorySceneRichEditor, NovelStorySceneReadOnly} from "./NovelStorySceneManuscript";
import {buildChildrensBookChapterSceneNavigationV1} from "../utils/childrensBookChapterSceneNavigationV1";
import {childrensBookTotalPhysicalPages} from "../utils/childrensBookPhysicalPagination";

// A real-React, read-only adapter. It reuses Novel's Book Workspace navigator,
// Writer Studio navigator and rich scene editor; no saving, copying or conversion.
// Existing manuscript unit IDs remain the sole navigation authority.
export default function ChildrensBookNovelParityV1E({
  title="", units=[], entries=[], chapters=[], lang="en", onReturn,
}) {
  const isFR=String(lang||"en").toLowerCase().startsWith("fr");
  const navigation=useMemo(()=>
    buildChildrensBookChapterSceneNavigationV1({units,chapters}),[units,chapters]);
  const [view,setView]=useState("book-structure");
  const [chapterId,setChapterId]=useState("");
  const [unitId,setUnitId]=useState("");
  const [writerStage,setWriterStage]=useState("write");
  const entriesById=useMemo(()=>new Map(
    (Array.isArray(entries)?entries:[])
      .filter(entry=>entry&&entry.unitId)
      .map(entry=>[String(entry.unitId),entry])),[entries]);
  const byId=useMemo(()=>new Map(
    (Array.isArray(units)?units:[])
      .filter(unit=>unit?.id).map(unit=>[String(unit.id),unit])),[units]);
  const sections=navigation.ok?navigation.sections:[];
  const chapterSections=sections.filter(section=>section.kind==="chapter");
  const manuscriptCount=sections.flatMap(section=>section.kind==="chapter"
    ? section.scenes.flatMap(scene=>scene.entries):section.kind==="unassigned"?section.entries:[]).length;
  const allEntries=sections.flatMap(section=>section.kind==="chapter"
    ? section.scenes.flatMap(scene=>scene.entries):section.entries);
  const activeUnit=allEntries.find(item=>item.unitId===unitId);
  const activeChapter=chapterSections.find(section=>section.key===chapterId);
  const selectedEntry=entriesById.get(unitId)||{};
  const front=sections.find(section=>section.kind==="front")?.entries||[];
  const back=sections.find(section=>section.kind==="back")?.entries||[];
  const unassigned=sections.find(section=>section.kind==="unassigned")?.entries||[];
  const textFor=(id)=>String(entriesById.get(id)?.manuscriptText||"");
  const openPage=(id)=>{
    if(!byId.has(id))return;
    setUnitId(id);setWriterStage("write");setView("writer-studio");
  };
  const row=(item)=>(
    <button key={item.unitId} className="ns-button ns-button-outline" type="button"
      data-testid="childrens-v1e-open-page" data-unit-id={item.unitId}
      onClick={()=>openPage(item.unitId)}>
      {item.pageLabel}{item.title?" · "+item.title:""}
    </button>
  );
  const backToScene=()=>{
    setWriterStage("write");
    setView(chapterId&&activeChapter?"chapter":"book-structure");
  };
  const panel={padding:22,borderRadius:20,border:"1px solid rgba(16,185,129,0.30)"};
  const heading=(k,t,d)=>(
    <div>
      <div style={{fontSize:11,fontWeight:900,letterSpacing:"0.12em",color:"#047857"}}>{k}</div>
      <h2 className="ns-section-title" style={{margin:"6px 0"}}>{t}</h2>
      {d?<p className="ns-card-meta">{d}</p>:null}
    </div>
  );
  if(!navigation.ok)return (
    <section className="ns-card" role="alert" data-testid="childrens-v1e-invalid">
      {isFR?"Navigation indisponible : identifiants ou organisation invalides.":
        "Navigation unavailable: invalid page identities or chapter organization."}
      <button type="button" className="ns-button ns-button-outline" onClick={onReturn}>
        {isFR?"Retour au Blueprint":"Back to Blueprint"}
      </button>
    </section>
  );
  if(view==="writer-studio") {
    return <div data-testid="childrens-v1e-writer-studio"
      className="ns-novel-story-workspace-v1e" style={{display:"grid",gap:16}}>
      <section className="ns-card" style={panel}>
        {heading("WRITER STUDIO",activeUnit?.title||activeUnit?.pageLabel||"Scene",
          (activeChapter?.title||"Manuscript")+" · "+(activeUnit?.pageLabel||""))}
        <button className="ns-button ns-button-outline" type="button"
          onClick={backToScene}>← {isFR?"Retour à la scène":"Back to Scene"}</button>
      </section>
      <NextScenesBookBlueprintNavigator
        projectTitle={title} currentWorkspace={writerStage} isFR={isFR}
        blueprintLabel="WRITER STUDIO"
        workspaces={[
          {id:"write",label:isFR?"Écrire":"Write",status:"waiting",statusLabel:isFR?"Lecture seule":"Read only"},
          {id:"review",label:isFR?"Réviser":"Review",status:"waiting",statusLabel:isFR?"Aperçu":"Preview"},
          {id:"collaboration",label:isFR?"Collaborer":"Collaborate",status:"waiting",statusLabel:isFR?"Plus tard":"Later",disabled:true},
          {id:"memory",label:isFR?"Soutien":"Story Support",status:"waiting",statusLabel:isFR?"Plus tard":"Later",disabled:true},
          {id:"reader",label:isFR?"Lecteurs":"Reader Response",status:"waiting",statusLabel:isFR?"Plus tard":"Later",disabled:true},
        ]}
        readyCount={0} statusTotal={5}
        onNavigate={(stage)=>{if(stage==="write"||stage==="review")setWriterStage(stage);}}
      />
      <section className="ns-card" style={panel}>
        {writerStage==="review"
          ? <div data-testid="childrens-v1e-review">
              {heading(isFR?"RÉVISION":"REVIEW",activeUnit?.title||"Scene")}
              <NovelStorySceneReadOnly text={textFor(unitId)}
                manuscriptContent={selectedEntry.manuscriptContent||null} lang={lang}/>
            </div>
          : <div data-testid="childrens-v1e-novel-editor">
              <p role="status" className="ns-card-meta">
                {isFR?"Aperçu en lecture seule · pas d'enregistrement":
                  "Read-only React review · no Save Scene or publication actions"}
              </p>
              <NovelStorySceneRichEditor
                id={unitId} title={activeUnit?.title||activeUnit?.pageLabel||""}
                text={textFor(unitId)} manuscriptContent={selectedEntry.manuscriptContent||null}
                lang={lang} disabled={true}/>
            </div>}
      </section>
    </div>;
  }
  return <div data-testid="childrens-v1e-book-workspace" style={{display:"grid",gap:16}}>
    <NovelStoryWorkspaceNavigator
      bookKind="childrens-book" physicalPageCount={childrensBookTotalPhysicalPages(units)}
      projectTitle={title} frontMatterCount={front.length}
      chapterCount={chapterSections.length} sceneCount={manuscriptCount}
      backMatterCount={back.length} manuscriptWorkspace={view}
      onNavigate={next=>{setView(next);setChapterId("");}}
      onBack={onReturn} isFR={isFR}
    />
    {view==="chapter"&&activeChapter?(
      <>
        <section className="ns-card" style={panel} data-testid="childrens-v1e-chapter">
          {heading(isFR?"CHAPITRE":"CHAPTER",activeChapter.title)}
          <button className="ns-button ns-button-outline" type="button"
            onClick={()=>{setChapterId("");setView("book-structure");}}>
            ← {isFR?"Retour à la structure du livre":"Back to Book Structure"}
          </button>
          <p className="ns-card-meta">
            {isFR?"Scènes du chapitre · les pages originales sont conservées":
              "Chapter Scenes · original page IDs and physical order preserved"}
          </p>
          {activeChapter.scenes.map(scene=><article
            className="ns-card ns-novel-story-structure-chapter-card" key={scene.key}
            style={{padding:16,marginTop:12,borderLeft:"7px solid #059669"}}>
            <h3>{scene.title}</h3>
            <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>{scene.entries.map(row)}</div>
          </article>)}
        </section>
      </>
    ):(
      <section className="ns-card" style={panel}
        data-testid={"childrens-v1e-"+view}>
        {view==="book-structure"?
          <>
            {heading(isFR?"STRUCTURE PRINCIPALE DU LIVRE":"MAIN BOOK STRUCTURE",
              isFR?"Organiser les chapitres":"Plan Your Chapters",
              isFR?"Les pages et doubles pages restent la structure physique du livre.":
                "Chapters are optional; existing pages and spreads remain the physical book structure.")}
            {front.length>0?<section><h3>FRONT MATTER</h3>{front.map(row)}</section>:null}
            {chapterSections.map((chapter,i)=><article
              key={chapter.key} className="ns-card ns-novel-story-structure-chapter-card"
              data-testid="childrens-v1e-chapter-card"
              style={{padding:18,margin:"12px 0",borderLeft:"7px solid #059669"}}>
              <div style={{fontSize:11,letterSpacing:"0.1em",fontWeight:900,color:"#047857"}}>
                {isFR?"CHAPITRE":"CHAPTER"} {i+1}
              </div>
              <h3>{chapter.title}</h3>
              <button type="button" className="ns-button ns-button-outline"
                onClick={()=>{setChapterId(chapter.key);setView("chapter");}}>
                {isFR?"Ouvrir le chapitre":"Open Chapter"}
              </button>
            </article>)}
            {unassigned.length>0?<section data-testid="childrens-v1e-unassigned">
              <h3>{isFR?"PAGES NON ATTRIBUÉES":"UNASSIGNED PAGES & SPREADS"}</h3>
              <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>{unassigned.map(row)}</div>
            </section>:null}
            {back.length>0?<section><h3>BACK MATTER</h3>{back.map(row)}</section>:null}
          </>
        :view==="write"?
          <>
            {heading(isFR?"ÉCRIRE":"WRITE",title,
              isFR?"Choisissez un chapitre ou une page pour continuer.":
                "Choose a Chapter to start or continue writing.")}
            {chapterSections.map(chapter=><button key={chapter.key}
              type="button" className="ns-button ns-button-outline"
              data-testid="childrens-v1e-choose-chapter"
              onClick={()=>{setChapterId(chapter.key);setView("chapter");}}>{chapter.title}</button>)}
            {unassigned.length>0?<div><h3>UNASSIGNED PAGES & SPREADS</h3>{unassigned.map(row)}</div>:null}
          </>
        :view==="review"?
          <>
            {heading("WHOLE-BOOK REVIEW",title)}
            {allEntries.map(item=><article key={item.unitId}
              style={{marginBottom:16,paddingBottom:12,borderBottom:"1px solid #ddd"}}>
              <h3>{item.pageLabel} {item.title}</h3>
              <NovelStorySceneReadOnly text={textFor(item.unitId)}
                manuscriptContent={entriesById.get(item.unitId)?.manuscriptContent||null} lang={lang}/>
            </article>)}
          </>
        :view==="page-layout"?
          <>
            {heading(isFR?"PAGES ET ILLUSTRATIONS":"PAGES & ILLUSTRATIONS",
              title,
              isFR?"Aucun changement de la géométrie ou des illustrations.":
                "Original page geometry and illustration references are unchanged.")}
            {allEntries.map(item=><div key={item.unitId}
              style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:12}}>
              <strong>{item.pageLabel} · {item.title||item.unitType}</strong>
              <span className="ns-card-meta">{byId.get(item.unitId)?.illustrationPlan||"No illustration"}</span>
            </div>)}
          </>
        :null}
      </section>
    )}
  </div>;
}
